/**
 * Settings > AI (approved mockup 2026-10-26): off until an admin turns it on, per company and per
 * feature, with a monthly cap. Claude through Amazon Bedrock in DocVault's own AWS account; it
 * only sees what the person may see, only suggests, and every use is counted.
 */
import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api, type AiSettingsT } from "../../lib/api";
import { useStore } from "../../lib/store";
import { coName } from "../../lib/brand";
import { Loading, Tile } from "../../components/bits";
import { PartTag } from "./SettingsLayout";

const LABEL: Record<string, string> = { ask: "Ask DocVault", brief: "Client brief", agreement_reader: "Read signed agreements", doc_details: "Fill in document details",
  what_changed: "What changed", import_mapping: "Match spreadsheet columns" };
const DESC: Record<string, string> = { ask: "Answers questions from your published documents and client records, with links to the source.",
  brief: "Adds a short summary and cautions from the notes to the client brief.", agreement_reader: "Suggests an agreement's dates, renewal, notice and amounts from the signed PDF.",
  doc_details: "Suggests a new document's title, type, area, description and acronyms.", what_changed: "Summarizes changes between versions.",
  import_mapping: "Maps an asset spreadsheet's columns for import." };

export function AiSettingsPage() {
  const { me, toast } = useStore();
  const codes = me.can_admin.filter((c) => c !== "CPY");
  const [sp, setSp] = useSearchParams();
  const co = codes.includes(sp.get("co") ?? "") ? sp.get("co")! : codes[0];
  const [d, setD] = useState<Awaited<ReturnType<typeof api.aiSettings>> | null>(null);
  const [caps, setCaps] = useState({ usd: "", tokens: "" });
  const load = async () => { if (co) { const r = await api.aiSettings(co); setD(r); setCaps({ usd: r.settings.monthly_cap_usd.toFixed(2), tokens: String(r.settings.monthly_cap_tokens) }); } };
  useEffect(() => { setD(null); load().catch((e: Error) => toast(e.message)); }, [co]);
  if (!co) return <Navigate to="/settings" replace />;
  const save = async (b: Partial<AiSettingsT>, msg: string) => {
    try { await api.setAiSettings({ company: co, ...b }); toast(msg); await load(); } catch (e) { toast((e as Error).message); }
  };
  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="account" />
        <h1 className="title">AI</h1>
        <div className="lede">Claude, through Amazon Bedrock in DocVault's own AWS account. Your documents stay in AWS and aren't used to train models. AI only suggests; people decide.</div>
      </div></div>
      {codes.length > 1 && (
        <div className="ctabs" role="tablist">
          {codes.map((c) => <button key={c} className={`ctab ${c === co ? "on" : ""}`} role="tab" aria-selected={c === co} onClick={() => setSp({ co: c }, { replace: true })}><Tile code={c} /><span>{coName(c)}</span></button>)}
        </div>
      )}
      {!d ? <Loading /> : <>
        {!d.configured && <div className="note info" style={{ display: "block", marginBottom: 14 }}><b>Not set up on the server yet.</b> The person who runs DocVault chooses the Bedrock model first. You can still set things up here.</div>}
        <div className="card sect">
          <div className="setrow"><div className="tx"><b>AI for {coName(co)}</b><span>When off, nobody in {coName(co)} sees AI buttons.</span></div>
            <label className="switch"><input type="checkbox" checked={d.settings.enabled} aria-label={`AI for ${coName(co)}`} onChange={(e) => void save({ enabled: e.target.checked }, e.target.checked ? "AI is on." : "AI is off.")} /><span /></label></div>
        </div>
        <div className="card sect" style={{ marginTop: 14, opacity: d.settings.enabled ? 1 : 0.6 }}>
          <h3>Features</h3>
          {Object.entries(d.features).map(([k, help]) => (
            <div key={k} className="setrow"><div className="tx"><b>{LABEL[k] ?? k}</b><span>{DESC[k] ?? help}</span></div>
              <label className="switch"><input type="checkbox" disabled={!d.settings.enabled} checked={d.settings.features[k] !== false} aria-label={LABEL[k] ?? k}
                onChange={(e) => void save({ features: { ...d.settings.features, [k]: e.target.checked } }, "Saved.")} /><span /></label></div>
          ))}
        </div>
        <div className="card sect" style={{ marginTop: 14 }}>
          <h3>This month</h3>
          <div className="kpis" style={{ gridTemplateColumns: "repeat(3,minmax(0,1fr))", margin: 0 }}>
            <div className="kc" style={{ ["--kc" as string]: "#2F6FB3" }}><span className="v">{d.usage.calls}</span><span className="l">AI uses</span></div>
            <div className="kc" style={{ ["--kc" as string]: "#1F9E8F" }}><span className="v">{d.prices_set ? `$${d.usage.cost_usd.toFixed(2)}` : "n/a"}</span><span className="l">{d.prices_set ? `of $${d.settings.monthly_cap_usd.toFixed(2)} cap` : "prices not set on the server"}</span></div>
            <div className="kc" style={{ ["--kc" as string]: "#7C5CC4" }}><span className="v">{(d.usage.tokens / 1e6).toFixed(1)}M</span><span className="l">of {(d.settings.monthly_cap_tokens / 1e6).toFixed(1)}M tokens</span></div>
          </div>
          <div className="form-row" style={{ marginTop: 12, alignItems: "flex-end" }}>
            <label><span className="lbl">Monthly cap (dollars)</span><input className="inp mono" style={{ width: 140 }} value={caps.usd} onChange={(e) => setCaps((x) => ({ ...x, usd: e.target.value }))} /></label>
            <label><span className="lbl">Monthly cap (tokens)</span><input className="inp mono" style={{ width: 160 }} value={caps.tokens} onChange={(e) => setCaps((x) => ({ ...x, tokens: e.target.value.replace(/[^\d]/g, "") }))} /></label>
            <button className="btn btn-ghost" onClick={() => void save({ monthly_cap_usd: Number(caps.usd), monthly_cap_tokens: Number(caps.tokens) }, "Caps saved.")}>Save caps</button>
          </div>
          <div className="help">When either cap is reached, AI stops for {coName(co)} until next month or until you raise it. Every use is in the audit log (feature and size, never the content).</div>
        </div>
      </>}
    </div>
  );
}
